import { useEffect, useState } from 'react'

export interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

// Reads CSS env(safe-area-inset-*) via a temporary DOM element.
// Falls back to 0 on non-iOS or when not in a WebView/PWA context.
function readInsets(): SafeAreaInsets {
  if (typeof document === 'undefined') return { top: 0, bottom: 0, left: 0, right: 0 }
  const el = document.createElement('div')
  el.style.cssText = [
    'position:fixed',
    'top:env(safe-area-inset-top,0px)',
    'bottom:env(safe-area-inset-bottom,0px)',
    'left:env(safe-area-inset-left,0px)',
    'right:env(safe-area-inset-right,0px)',
    'pointer-events:none',
    'visibility:hidden',
  ].join(';')
  document.body.appendChild(el)
  const cs = getComputedStyle(el)
  const insets = {
    top:    parseFloat(cs.top)    || 0,
    bottom: parseFloat(cs.bottom) || 0,
    left:   parseFloat(cs.left)   || 0,
    right:  parseFloat(cs.right)  || 0,
  }
  document.body.removeChild(el)
  return insets
}

export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>({ top: 0, bottom: 0, left: 0, right: 0 })

  useEffect(() => {
    setInsets(readInsets())

    // Re-read on orientation change (inset bottom changes on iPhone landscape)
    const handler = () => setInsets(readInsets())
    window.addEventListener('resize', handler)
    window.addEventListener('orientationchange', handler)
    return () => {
      window.removeEventListener('resize', handler)
      window.removeEventListener('orientationchange', handler)
    }
  }, [])

  return insets
}
